import * as React from 'react';
import { Loader, t, TextSubtitle3RMMixin } from '@mobalytics/shared';
import { Redirect, useLocation } from 'react-router-dom';
import { css } from '@emotion/react';
import { Mixpanel } from '../../marketing/mixpanel';
import { useAccount } from '../payment/hooks/account.hook';
import { genPaymentV2ErrorPath, genPaymentV2Path, genRedirectUrl } from '../../utils/formatter/path.formatter';
import { MascotLayout } from '../../ui/layout/mascot-layout/mascot-layout.component';
import { TreasuryPaymentsProvider } from '../../__generated/treasury/types';
import { FullScreenCenter } from '../../ui/layout/full-screen-center/full-screen-center.component';
import { usePaymentV2UrlParams } from './hooks/payment-filters.hook';
import { PaymentV2 } from './payment-v2.component';
import { useSignInRedirect } from './hooks/sing-in-redirect.hook';
import { usePaymentV2PageData } from './hooks/payment-page-data.hook';
import { Error } from './error/error.component';
import { PaymentError } from './payment-error.types';

export const PaymentContainer: React.FC = () => {
  const { productId, priceId, checkout, productAbbreviation, redirectStatus } = usePaymentV2UrlParams();
  const { account, isDataLoaded } = useAccount();
  const result = usePaymentV2PageData({
    productId,
    priceId,
    productAbbreviation,
  });

  const { search } = useLocation();

  React.useEffect(() => {
    if (account?.uid) {
      Mixpanel.onUserStatusChanged(account);
    }
  }, [account]);

  useSignInRedirect(isDataLoaded, !!account?.uid);

  const returnBackToApp = () => {
    window.location.href = genRedirectUrl();
  };

  if (result.status === 'loading') {
    return (
      <FullScreenCenter>
        <Loader />
      </FullScreenCenter>
    );
  }

  if (result.status === 'error') {
    return (
      <FullScreenCenter>
        <Error productId={productId} priceId={priceId} errorCode={PaymentError.INTERNAL_ERROR} />
      </FullScreenCenter>
    );
  }

  const { product, paypal } = result;

  if (!priceId) {
    return <Redirect to={genPaymentV2Path(search, productId, product.price.id, undefined, undefined)} />;
  }

  if (checkout?.state === 'success' && redirectStatus === 'failed') {
    return <Redirect to={genPaymentV2ErrorPath(productId, priceId, search, PaymentError.PAYMENT_DECLINED)} />;
  }

  if (!checkout && product.__typename === 'TreasuryProductBattlePass' && product.isAlreadyPaid) {
    return (
      <FullScreenCenter>
        <MascotLayout
          title="Reward Pass owned"
          description="Premium Reward Pass is already active"
          mascotEmotion="calm"
        >
          <p css={linkCss} onClick={returnBackToApp}>
            {t('Go back')}
          </p>
        </MascotLayout>
      </FullScreenCenter>
    );
  }

  return (
    <FullScreenCenter>
      <PaymentV2
        product={product}
        checkout={checkout}
        paymentMethod={paypal ? TreasuryPaymentsProvider.BRAINTREE : TreasuryPaymentsProvider.STRIPE}
      />
    </FullScreenCenter>
  );
};

const linkCss = css`
  ${TextSubtitle3RMMixin};
  cursor: pointer;
  color: var(--general-gold-300);
`;
